import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { useStore } from "@Store/";

import Container from "@components/Container";
import RichText from "@components/RichText";

import {
  MissionInfoContent,
  MissionInfoItem,
  MissionInfoItemContent,
  MissionInfoLine,
  MissionInfoList,
  MissionInfoMain,
} from "./index.style";

gsap.registerPlugin(ScrollTrigger);

const OurMissionInfo = ({ list, id }) => {
  const [store] = useStore();

  const sectionRef = useRef(null);
  const lineRef = useRef(null);
  const itemsRef = useRef([]);
  const itemContentsRef = useRef([]);
  const smoothScrollRef = useRef(store?.smoothScroll);

  const sectionId = id || "our-mission-info";

  smoothScrollRef.current = store?.smoothScroll;

  const addItemRef = (element, index) => {
    if (!element) return;

    itemsRef.current[index] = element;
  };

  const addItemContentRef = (element, index) => {
    if (!element) return;

    itemContentsRef.current[index] = element;
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const section = sectionRef.current;
    const line = lineRef.current;
    const items = itemsRef.current.filter(Boolean);
    const itemContents = itemContentsRef.current.filter(Boolean);

    if (
      !section ||
      !line ||
      !items.length ||
      !itemContents.length
    ) {
      return undefined;
    }

    const refresh = () => {
      if (smoothScrollRef.current) {
        smoothScrollRef.current.refresh();
      } else {
        ScrollTrigger.refresh();
      }
    };

    const resize = () => {
      gsap.matchMediaRefresh();
      refresh();
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {

        const itemTweens = itemContents.map(
          (itemContent, index) => {
            const triggerItem = items[index];
            const isLeft = index % 2 === 0;

            if (!triggerItem) return null;

            return gsap.fromTo(
              itemContent,
              {
                x: isLeft ? -100 : 100,
                autoAlpha: 0,
              },
              {
                x: 0,
                autoAlpha: 1,
                duration: 1,
                ease: "power3.out",
                clearProps: "transform,opacity,visibility",
                scrollTrigger: {
                  trigger: triggerItem,
                  start: "top 82%",
                  once: true,
                  invalidateOnRefresh: true,
                },
              },
            );
          },
        );

        requestAnimationFrame(refresh);

        return () => {

          itemTweens.forEach((tween) => {
            if (tween) {
              tween.kill();
            }
          });
        };
      });

      mm.add("(max-width:1023px)", () => {
        gsap.set([line, ...itemContents], {
          clearProps: "all",
        });
      });

      requestAnimationFrame(refresh);

      return () => {
        mm.revert();
      };
    }, section);

    window.addEventListener("load", refresh);
    window.addEventListener("resize", resize);

    return () => {
      ctx.revert();

      window.removeEventListener("load", refresh);
      window.removeEventListener("resize", resize);

      ScrollTrigger.clearScrollMemory();

      itemsRef.current = [];
      itemContentsRef.current = [];
    };
  }, [list]);

  return (
    <MissionInfoMain ref={sectionRef} id={sectionId}>
      <Container>
        <MissionInfoContent>
          <MissionInfoLine ref={lineRef} aria-hidden="true" />

          <MissionInfoList>
            {list.map((item, index) => (
              <MissionInfoItem
                ref={(element) => addItemRef(element, index)}
                key={item?.id || item?.contentful_id || index}
                $isLeft={index % 2 === 0}
                style={{
                  "--top-margin": `${index * 320}px`,
                }}
              >
                <MissionInfoItemContent
                  ref={(element) =>
                    addItemContentRef(element, index)
                  }
                >
                  <RichText content={item?.text} />
                </MissionInfoItemContent>
              </MissionInfoItem>
            ))}
          </MissionInfoList>
        </MissionInfoContent>
      </Container>
    </MissionInfoMain>
  );
};

OurMissionInfo.propTypes = {
  id: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      contentful_id: PropTypes.string,
      text: PropTypes.object,
    }),
  ),
};

OurMissionInfo.defaultProps = {
  id: "our-mission-info",
  list: [],
};

export default OurMissionInfo;